// Pure formatting / geometry helpers for rendering measurement labels
// and tooltips on the map. Extracted out of MapView.js so they can be
// reused (and unit-tested) without dragging in the rest of the map
// orchestration code. No React, no closures over component state.
//
// `isMarker` is imported from mapUtils for shape-type detection in
// `getShapePosition` — we deliberately keep the dependency arrow
// pointing the same direction as the existing extracted helpers.

import { isMarker } from './mapUtils';

// Format a measurement's primary value for the on-shape badge that
// floats at the centre of every visible shape. Returns '' for shape
// types that don't carry a primary value (markers / text labels).
export function formatShapeLabel(m) {
  const mt = m.measurement_type;
  const st = m.shape_type || mt;
  const v = m.value || 0;
  if (mt === 'area') {
    if (v >= 1000000) return `${(v / 1000000).toFixed(2)} km\u00B2`;
    return `${v.toFixed(1)} m\u00B2`;
  }
  if (mt === 'distance' || mt === 'perimeter') {
    return v >= 1000 ? `${(v / 1000).toFixed(2)} km` : `${v.toFixed(1)} m`;
  }
  if (mt === 'angle') return `${v.toFixed(1)}\u00B0`;
  if (mt === 'height') return `~${v.toFixed(1)} m`;
  if (st === 'marker' || st === 'text_label') return '';
  return '';
}

// Build the HTML body shown inside the Google Maps InfoWindow when the
// user hovers a shape. Inline styles only (no Tailwind) because the
// InfoWindow lives outside the React tree.
export function buildTooltipHTML(m) {
  const clr = m.color || '#7367F0';
  const st = m.shape_type || m.measurement_type || '';
  const typeLabel = st.charAt(0).toUpperCase() + st.slice(1).replace(/_/g, ' ');
  const lines = (m.details || '').split('\n').filter((l) => l && !l.startsWith('Type:'));
  return `<div style="font-family:'Public Sans',sans-serif;min-width:160px;max-width:260px;">` +
    `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">` +
      `<span style="width:10px;height:10px;border-radius:3px;background:${clr};flex-shrink:0;"></span>` +
      `<span style="font-weight:700;font-size:13px;color:#2D2B3A;">${m.label || 'Shape'}</span>` +
    `</div>` +
    (m.description ? `<div style="font-size:11px;color:#7C7891;margin-bottom:6px;line-height:1.4;">${m.description}</div>` : '') +
    `<div style="font-size:10px;color:#9E9AAF;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;margin-bottom:4px;">${typeLabel}</div>` +
    lines.map((l) => `<div style="font-size:12px;color:#4A4458;line-height:1.5;">${l.replace(/\u00B2/g, '&#178;')}</div>`).join('') +
  `</div>`;
}

// Return a representative LatLng for the centre of any shape kind we
// draw on the map. Used by the tooltip + on-shape label code paths.
// Returns null if no sensible centre can be derived (e.g. an empty
// polyline path).
export function getShapePosition(shape, type) {
  if (type === 'circle') return shape.getCenter();
  if (type === 'rectangle' && shape.getBounds) {
    const b = shape.getBounds();
    return b ? b.getCenter() : null;
  }
  if (isMarker(shape)) return shape.getPosition();
  if (shape.getPath) {
    const p = shape.getPath();
    if (p.getLength() > 0) {
      let lat = 0, lng = 0;
      for (let i = 0; i < p.getLength(); i++) {
        lat += p.getAt(i).lat();
        lng += p.getAt(i).lng();
      }
      return new window.google.maps.LatLng(lat / p.getLength(), lng / p.getLength());
    }
  }
  return null;
}
